import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';
import { FieldGroupModel, FieldGroupPlacement } from 'src/shared/models/field/field-group';
import { AuthenticatedHttpService, OfflineWrapResult } from './authenticatedHttpService.service';
import { Option } from 'src/shared/models/field/option';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { FieldModel, FieldType, TextFieldVariant } from 'src/shared/models/field/field';
import { dateRangeDelimiter } from 'src/constants/defaults';
import * as fp from 'lodash/fp';

@Injectable({ providedIn: 'root' })
export class FieldProvider {
	static FIELDS = "FieldProvider_FIELDS";

	loading$ = new BehaviorSubject(false);

	private _fieldGroups = new ReplaySubject<Array<FieldGroupModel>>();
	public fieldGroups$ = this._fieldGroups.asObservable();

	initRequired: boolean | null = true;

	sub: Subscription;
	requestPageEnabled$ = new BehaviorSubject(false); // Optimization
	private fillPageEnabled(fieldGroups: Array<FieldGroupModel>) {
		const fgs = (fieldGroups || [])
			.filter(f => f.placement === FieldGroupPlacement.CreateNotificationRequestPage);

		const options = fgs
			.map(fg => fg.fields || [])
			.reduce((acc, cur) => [...acc, ...cur], [])
			.map(f => f.options || [])
			.reduce((acc, cur) => [...acc, ...cur], []) as Option[];

		const optionIds = options.map(o => o.id);
		const fgOptions = fgs.map(fg => fg.optionId).filter(o => o);

		this.requestPageEnabled$.next(fp.intersection(optionIds, fgOptions).length > 0);
	}

	constructor(
		private http: AuthenticatedHttpService,
		private storage: LocalStorageService
	) {
		this.sub = this.fieldGroups$.subscribe(fgs => this.fillPageEnabled(fgs));
	}

	async init(hideCurrent = true) {
		try {
			this.loading$.next(true);
			this.initRequired = null;

			if (hideCurrent) // So that user doesn't interact with 'reloading controls'
				this._fieldGroups.next([]);

			const res = await this.http.wrapIntoOffline<Array<FieldGroupModel>>({
				fetch: this.http.get,
				params: [environment.web.identity + "/api/fields"],
				storeKey: FieldProvider.FIELDS
			});

			const normalizeName = (name: string): string =>
				(name ?? '').trim().toLowerCase().replace(/:$/, '');

			const fields = (res.result || []).map(f => {
				const group = FieldGroupModel.fromAny(f);

				this.replaceVonBisWithRangeField(group);

				this.removeUnknownFieldTypes(group);

				this.sortFieldsByOrder(group);

				return group;
			});

			this._fieldGroups.next(fields);
			this.initRequired = res.status === OfflineWrapResult.Offline;
		} finally {
			this.loading$.next(false);
		}
	}

	async purge() {
		this._fieldGroups.complete();
		this._fieldGroups = new ReplaySubject<Array<FieldGroupModel>>();
		this.fieldGroups$ = this._fieldGroups.asObservable();

		await this.storage.remove(FieldProvider.FIELDS);
		this.initRequired = null;
	}

	private replaceVonBisWithRangeField(group: FieldGroupModel): void {
		const dateFields = group.fields?.filter(f => f.type === FieldType.DateTime) || [];

		const von = dateFields.find(f => this.normalizeName(f.name).startsWith('von') || this.normalizeName(f.name).endsWith('von'));
		const bis = dateFields.find(f => this.normalizeName(f.name).startsWith('bis') || this.normalizeName(f.name).endsWith('bis'));

		if (!von || !bis) return;

		const hasTime =
			(von.pickerFormat ?? '').includes('HH') ||
			(bis.pickerFormat ?? '').includes('HH');

		const rangeType = hasTime ? FieldType.DateTimeRange : FieldType.DateRange;

		const newRangeField = new FieldModel(
			von.id,
			von.order,
			rangeType,
			`${von.name}${dateRangeDelimiter}${bis.name}`,
			'',
			'',
			'',
			von.creator,
			von.title,
			von.createdAt,
			von.isRead,
			TextFieldVariant.String,
			[],
			von.displayFormat,
			von.pickerFormat
		);

		group.fields = group.fields?.filter(f => f !== von && f !== bis);
		group.fields.push(newRangeField);
	}

	private removeUnknownFieldTypes(group: FieldGroupModel): void {
		group.fields = group.fields?.filter(f => Object.values(FieldType).includes(f.type));
	}

	private sortFieldsByOrder(group: FieldGroupModel): void {
		group.fields?.sort((a, b) => a.order - b.order);
	}

	private normalizeName(name: string): string {
		return (name ?? '').trim().toLowerCase().replace(/:$/, '');
	}

	ionViewWillLeave(): void {
		if (this.sub) this.sub.unsubscribe();
	}
}
